import { Plus, Trash } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);

/**
 * WorkDescriptionsEditor — reusable list editor for a WO's dated work-done
 * entries. Each row has a date + a free-text line of what was done. Use with
 * both main WOs and sub-tasks:
 *
 *   <WorkDescriptionsEditor
 *     value={form.descriptions}
 *     onChange={(next) => setForm({ ...form, descriptions: next })}
 *     testidPrefix="parent-desc"
 *   />
 *
 * `value` is `[{ text, date }]`. Pass an empty array to start fresh; the
 * component itself never defaults a row — the + button adds the first one.
 */
export default function WorkDescriptionsEditor({ value = [], onChange, label = "Work descriptions", testidPrefix = "wo-desc" }) {
    const rows = Array.isArray(value) ? value : [];
    const update = (idx, patch) => {
        const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
        onChange?.(next);
    };
    const add = () => onChange?.([...rows, { text: "", date: today() }]);
    const remove = (idx) => onChange?.(rows.filter((_, i) => i !== idx));

    return (
        <div data-testid={`${testidPrefix}-editor`}>
            <div className="flex items-center justify-between mb-1.5">
                <div className="label-mono">{label}</div>
                <button
                    type="button"
                    onClick={add}
                    className="inline-flex items-center gap-1 text-xs text-fleet-ink hover:text-fleet-ink/80 border border-fleet-border rounded-md px-2 py-1"
                    data-testid={`${testidPrefix}-add`}
                >
                    <Plus size={12} weight="bold" /> Add description
                </button>
            </div>

            {rows.length === 0 ? (
                <div className="text-xs text-fleet-inkSoft italic border border-dashed border-fleet-border rounded-md px-3 py-2.5"
                     data-testid={`${testidPrefix}-empty`}>
                    No work descriptions yet — click <span className="font-semibold">+ Add description</span> to log what was done.
                </div>
            ) : (
                <div className="space-y-2">
                    {rows.map((row, idx) => (
                        <div key={idx} className="grid grid-cols-[auto_1fr_auto] gap-2 items-start" data-testid={`${testidPrefix}-row-${idx}`}>
                            <input
                                type="date"
                                value={row.date || today()}
                                onChange={(e) => update(idx, { date: e.target.value })}
                                className="px-2 py-1.5 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink font-mono"
                                data-testid={`${testidPrefix}-date-${idx}`}
                            />
                            <textarea
                                value={row.text || ""}
                                onChange={(e) => update(idx, { text: e.target.value })}
                                placeholder="What was done on this day…"
                                rows={2}
                                className="w-full px-3 py-1.5 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink resize-y whitespace-pre-wrap"
                                data-testid={`${testidPrefix}-text-${idx}`}
                            />
                            <button
                                type="button"
                                onClick={() => remove(idx)}
                                className="p-1.5 text-fleet-inkSoft hover:text-rose-600 border border-fleet-border rounded-md"
                                aria-label="Remove"
                                data-testid={`${testidPrefix}-remove-${idx}`}
                            >
                                <Trash size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
